<template>
  <div class="bui-tbl-detail-count" role="group" aria-label="Result count and page size">
    <span class="count">{{ formattedCount }}</span>

    <span v-if="showLimit" class="divider" aria-hidden="true" />

    <TblPageLimitSelector
      v-if="showLimit"
      class="limit"
      :model-value="limit"
      :options="limitOptions"
      :suffix="perPageSuffix"
      @update:model-value="onLimitChange"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Combined "X items | 10 per page" molecule that sits ABOVE the table per
 * Figma frame `bui-media` -> `tblDetailCount` (1335:8007). Owns both the
 * count copy and the page-limit dropdown so the bottom bar can stay focused
 * on bulk actions + pagination only.
 */
import { computed } from 'vue';
import TblPageLimitSelector from '../TblPageLimitSelector/TblPageLimitSelector.vue';

type Option = { label: string; value: number | string };

const props = withDefaults(
  defineProps<{
    count?: number;
    label?: string;
    pluralLabel?: string;
    loading?: boolean;
    /** When provided, the inline page-limit dropdown is rendered. */
    limit?: number | string | null;
    limitOptions?: Array<number | string | Option>;
    perPageSuffix?: string;
  }>(),
  {
    count: 0,
    label: 'item',
    pluralLabel: 'items',
    loading: false,
    limit: null,
    limitOptions: () => [10, 25, 50, 100],
    perPageSuffix: 'per page',
  },
);

const emit = defineEmits<{ 'update:limit': [value: number | string] }>();

const formattedCount = computed(() => {
  if (props.loading) return '…';
  const word = props.count === 1 ? props.label : props.pluralLabel;
  return `${props.count.toLocaleString()} ${word}`;
});

const showLimit = computed(() => props.limit != null);

function onLimitChange(v: number | string) {
  emit('update:limit', v);
}
</script>

<style lang="scss" scoped>
.bui-tbl-detail-count {
  display: inline-flex;
  align-items: center;
  gap: var(--bui-tbl-detail-gap, 12px);
  padding-left: var(--bui-tbl-detail-pad-x, 16px);
  min-height: var(--bui-tbl-detail-h, 16px);
  color: var(--bui-tbl-detail-text, #ffb2db);
  font-family: 'Robout', system-ui, sans-serif;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .bui-tbl-detail-count {
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    row-gap: 0;
    padding-left: 10px;
    padding-right: 10px;
    font-size: 11px;
    line-height: 14px;
    white-space: nowrap;
    min-height: 0;
  }

  .count {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .limit {
    flex: 0 0 auto;
    margin-left: auto;
  }

  .divider {
    height: 10px;
  }
}

.count {
  color: inherit;
}

.divider {
  display: inline-block;
  width: 1px;
  height: 12px;
  background: var(--bui-tbl-detail-divider, rgba(255, 178, 219, 0.5));
  flex-shrink: 0;
}

.limit {
  /* The selector inherits its own colors via --bui-droplet-text which is
     also pink (#ffb2db) per Figma `tblDetailCountDropdown`. */
}
</style>
